import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { publicAPI } from '../api';
import { Trophy, ChevronLeft, ChevronRight, Sun, Moon, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const MONTH_NAMES = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const PublicLeaderboard = () => {
    const { groupSlug } = useParams(); // Changed from groupId to groupSlug
    const navigate = useNavigate();
    const { isDark, toggleTheme } = useTheme();
    
    const [leaderboard, setLeaderboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableMonths, setAvailableMonths] = useState([]);

    useEffect(() => {
        if (groupSlug) {
            loadLeaderboard();
            loadAvailableMonths();
        }
    }, [groupSlug, selectedMonth, selectedYear]);

    const loadLeaderboard = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await publicAPI.getLeaderboard(groupSlug, selectedMonth, selectedYear);
            setLeaderboard(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Gagal memuat klasemen');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableMonths = async () => {
        try {
            const data = await publicAPI.getAvailableMonths(groupSlug);
            setAvailableMonths(data.months || []);
        } catch (err) {
            console.error('Failed to load available months');
        }
    };

    const handlePrevMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    };

    const getScoreColorClass = (score) => {
        if (score > 0) return 'text-neon-green';
        if (score < 0) return 'text-red-500';
        return 'text-zinc-400';
    };

    const getRankBadge = (rank) => {
        if (rank === 1) return '🥇';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return `#${rank}`;
    };

    return (
        <div className={`min-h-screen ${isDark ? 'bg-void-black' : 'bg-cream-light'}`}>
            {/* Header */}
            <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-void-black/95 border-zinc-800' : 'bg-cream-light/95 border-zinc-300'} backdrop-blur-sm`}>
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className={isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-600 hover:text-black'}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Kembali
                        </Button>
                        <div className="flex items-center gap-2">
                            <Trophy className="w-6 h-6 text-neon-green" />
                            <h1 className={`text-xl font-headings font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                                {leaderboard?.group_name || 'Klasemen'}
                            </h1>
                        </div>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className={`p-2 rounded-md ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-200'} transition-colors`}
                    >
                        {isDark ? (
                            <Sun className="w-5 h-5 text-yellow-400" />
                        ) : (
                            <Moon className="w-5 h-5 text-zinc-600" />
                        )}
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-4 py-8">
                {error ? (
                    <div className={`text-center py-12 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg">{error}</p>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/')}
                            className="mt-4"
                        >
                            Kembali ke Login
                        </Button>
                    </div>
                ) : loading ? (
                    <div className="flex items-center justify-center py-12">
                        <span className="spinner" />
                    </div>
                ) : leaderboard ? (
                    <div className="space-y-6">
                        {/* Month Selector */}
                        <div className={`stat-card flex items-center justify-between ${!isDark ? 'bg-white border-zinc-300' : ''}`}>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handlePrevMonth}
                                className={isDark ? 'border-zinc-700' : 'border-zinc-300'}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <div className="flex items-center gap-3">
                                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                                    <SelectTrigger className={`w-32 ${isDark ? 'border-zinc-700' : 'border-zinc-300'}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTH_NAMES.map((name, idx) => (
                                            <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                                    <SelectTrigger className={`w-24 ${isDark ? 'border-zinc-700' : 'border-zinc-300'}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[2024, 2025, 2026, 2027].map((y) => (
                                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleNextMonth}
                                className={isDark ? 'border-zinc-700' : 'border-zinc-300'}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Rankings */}
                        <div className={`stat-card ${!isDark ? 'bg-white border-zinc-300' : ''}`}>
                            <h2 className={`text-lg font-headings font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-black'}`}>
                                <Trophy className="w-5 h-5 text-neon-green" />
                                Peringkat - {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                            </h2>
                            
                            {leaderboard.entries.length === 0 ? (
                                <p className="text-center text-zinc-500 py-8">Belum ada data</p>
                            ) : (
                                <div className="space-y-2">
                                    {leaderboard.entries.map((entry, idx) => (
                                        <div 
                                            key={entry.user_id}
                                            className={`flex items-center justify-between p-3 rounded-lg ${
                                                idx === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                                                idx === 1 ? 'bg-zinc-400/10 border border-zinc-400/30' :
                                                idx === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
                                                isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-zinc-100 border border-zinc-200'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{getRankBadge(idx + 1)}</span>
                                                <span className={`font-bold uppercase ${isDark ? 'text-white' : 'text-black'}`}>
                                                    {entry.username.toUpperCase()}
                                                </span>
                                            </div>
                                            <span className={`text-xl font-mono font-bold ${getScoreColorClass(entry.total_score)}`}>
                                                {entry.total_score > 0 ? '+' : ''}{entry.total_score.toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Score Cards */}
                        {leaderboard.scorecards.length > 0 && (
                            <div className={`stat-card ${!isDark ? 'bg-white border-zinc-300' : ''}`}>
                                <h2 className={`text-lg font-headings font-semibold mb-4 ${isDark ? 'text-white' : 'text-black'}`}>
                                    Score Cards ({leaderboard.scorecards.length})
                                </h2>
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {leaderboard.scorecards.map((sc, cardIndex) => {
                                        const isSettled = sc.is_settled;
                                        return (
                                            <div 
                                                key={sc.id} 
                                                className={`stat-card relative ${!isDark ? 'bg-zinc-50 border-zinc-200' : ''}`}
                                                style={isSettled ? { backgroundColor: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.3)' } : {}}
                                            >
                                                {/* Score Card Number */}
                                                <div className={`absolute -top-2 -left-2 w-8 h-8 rounded-full ${isSettled ? 'bg-blue-500' : 'bg-neon-green'} flex items-center justify-center shadow-lg`}>
                                                    <span className="text-sm font-bold text-void-black">#{leaderboard.scorecards.length - cardIndex}</span>
                                                </div>
                                                {/* Settlement Badge */}
                                                {isSettled && (
                                                    <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-lg">
                                                        ✓ Settled
                                                    </div>
                                                )}
                                                <div className="pl-6 pt-1">
                                                    <p className={`text-xs font-mono ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>
                                                        {formatDate(sc.created_at)} {formatTime(sc.created_at)}
                                                    </p>
                                                    <p className={`text-xs mb-2 ${isDark ? 'text-zinc-600' : 'text-zinc-500'}`}>
                                                        by <span className="font-bold uppercase">{sc.created_by_username.toUpperCase()}</span>
                                                    </p>
                                                </div>
                                                <div className="space-y-1">
                                                    {sc.entries.map((entry, idx) => {
                                                        const isFinanceSettled = sc.finance_settled?.includes(entry.user_id) || false;
                                                        return (
                                                            <div 
                                                                key={idx} 
                                                                className={`flex justify-between items-center py-1 px-2 -mx-2 rounded border-b last:border-0 ${
                                                                    isDark ? 'border-zinc-800' : 'border-zinc-200'
                                                                } ${isFinanceSettled ? 'bg-yellow-500/20' : ''}`}
                                                            >
                                                                <div className="flex items-center gap-1">
                                                                    <span className={`text-sm font-bold uppercase ${isDark ? 'text-white' : 'text-black'}`}>
                                                                        {entry.username.toUpperCase()}
                                                                    </span>
                                                                    {isFinanceSettled && (
                                                                        <span className="text-xs text-yellow-400">💰</span>
                                                                    )}
                                                                </div>
                                                                <span className={`text-sm font-mono font-semibold ${getScoreColorClass(entry.score)}`}>
                                                                    {entry.score > 0 ? '+' : ''}{entry.score.toLocaleString()}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </main>

            {/* Footer */}
            <footer className={`text-center py-4 border-t ${isDark ? 'border-zinc-800 text-zinc-600' : 'border-zinc-300 text-zinc-500'}`}>
                <p className="text-sm">Virtual Games Wallet</p>
            </footer>
        </div>
    );
};

export default PublicLeaderboard;
